// Hashtags and @mentions inside memories, comments and ideas.
// Anyone creates a new hashtag just by writing it: "#snowday". Tags are stored normalised.

export const MAX_TAGS_PER_POST = 10;
const TAG_RE = /(^|[^\p{L}\p{N}_#])#([\p{L}\p{N}_]{2,40})(?![\p{L}\p{N}_])/gu;
const MENTION_RE = /(^|[^\p{L}\p{N}_@.])@([a-z0-9._]{3,20})(?![a-z0-9_])/giu;

/** "#SnowDay" and "#snowday" are the same tag. */
export const normalizeTag = (raw: string): string => raw.replace(/^#/, '').normalize('NFC').toLowerCase();
export const isValidTag = (raw: string): boolean => /^[\p{L}\p{N}_]{2,40}$/u.test(normalizeTag(raw));

/** Unique, normalised tags in order of first use, capped at MAX_TAGS_PER_POST. */
export function extractTags(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(TAG_RE)) {
    const t = normalizeTag(m[2]!);
    if (!out.includes(t)) out.push(t);
    if (out.length === MAX_TAGS_PER_POST) break;
  }
  return out;
}

export type Segment = { kind: 'text'; value: string } | { kind: 'tag'; value: string; tag: string } | { kind: 'mention'; value: string; handle: string };

/** Splits text into plain parts, #tags and @mentions, so React can render links without any HTML. */
export function segments(text: string): Segment[] {
  const hits: { start: number; end: number; seg: Segment }[] = [];
  for (const m of text.matchAll(TAG_RE)) {
    const start = m.index! + m[1]!.length;
    hits.push({ start, end: start + 1 + m[2]!.length, seg: { kind: 'tag', value: '#' + m[2], tag: normalizeTag(m[2]!) } });
  }
  for (const m of text.matchAll(MENTION_RE)) {
    const start = m.index! + m[1]!.length;
    hits.push({ start, end: start + 1 + m[2]!.length, seg: { kind: 'mention', value: '@' + m[2], handle: m[2]!.toLowerCase() } });
  }
  hits.sort((a, b) => a.start - b.start);
  const out: Segment[] = [];
  let pos = 0;
  for (const h of hits) {
    if (h.start < pos) continue;
    if (h.start > pos) out.push({ kind: 'text', value: text.slice(pos, h.start) });
    out.push(h.seg);
    pos = h.end;
  }
  if (pos < text.length) out.push({ kind: 'text', value: text.slice(pos) });
  return out;
}

/** Stable rainbow colour for a tag or name, so #snowday is always the same colour. */
export const RAINBOW = ['pink', 'coral', 'orange', 'amber', 'yellow', 'lime', 'mint', 'aqua', 'sky', 'periwinkle', 'violet', 'bubblegum'] as const;
export type RainbowName = (typeof RAINBOW)[number];
export function colorFor(key: string): string {
  let h = 2166136261;
  for (const ch of key) h = Math.imul(h ^ ch.codePointAt(0)!, 16777619);
  return `var(--rb-${RAINBOW[(h >>> 0) % RAINBOW.length]})`;
}

export function slugify(name: string): string {
  return name.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30);
}
